import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Compass,
  IdCard,
  Leaf,
  MapPin,
  MinusCircle,
  Receipt,
  Ruler,
  ShieldCheck,
  Trees,
  type LucideIcon,
} from 'lucide-react';
import { Chip } from '@/ui/primitives';
import { cn } from '@/lib/utils';

interface Props {
  imovel: Record<string, unknown> | null | undefined;
  verificacao: string | null | undefined;
}

// Campos do cadastro que, quando ausentes, podem ser a causa real
// do sinal detectado pelo Diagnóstico. Se faltar algum desses,
// o analista vê o alerta no topo: "esse sinal pode ser falta de cadastro".
const CAMPOS_CRITICOS: Record<string, string[]> = {
  sicar: ['car_codigo', 'car_registro', 'car_area_consolidada'],
  sigef: ['sigef_codigo', 'geo_codigo_sigef', 'geo_status', 'datum', 'art_numero', 'perimetro_m'],
  sobreposicao: ['matricula', 'sigef_codigo', 'datum', 'confrontacoes'],
  prodes: ['car_codigo', 'car_latitude', 'car_longitude', 'coordenadas'],
  cnfp: ['matricula', 'geo_codigo_sigef', 'tipo_vinculo_dominial'],
  ccir_externo: ['ccir_codigo_imovel', 'ccir_numero', 'ccir_data_vencimento', 'incra'],
  icmbio_autos: ['coordenadas', 'car_codigo'],
  icmbio_embargos: ['coordenadas', 'car_codigo'],
  ibama_embargo_geo: ['coordenadas', 'car_codigo'],
  embargo_estadual: ['coordenadas', 'car_codigo'],
};

interface Secao {
  titulo: string;
  icon: LucideIcon;
  campos: Array<{ key: string; label: string }>;
}

const SECOES: Secao[] = [
  {
    titulo: 'Identificação',
    icon: IdCard,
    campos: [
      { key: 'denominacao', label: 'Denominação' },
      { key: 'matricula', label: 'Matrícula' },
      { key: 'cib', label: 'CIB' },
      { key: 'incra', label: 'INCRA' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'tipologia', label: 'Tipologia' },
      { key: 'gleba', label: 'Gleba' },
      { key: 'lote', label: 'Lote' },
      { key: 'tipo_vinculo_dominial', label: 'Vínculo dominial' },
    ],
  },
  {
    titulo: 'Localização',
    icon: MapPin,
    campos: [
      { key: 'municipio', label: 'Município' },
      { key: 'uf', label: 'UF' },
      { key: 'cep_imovel', label: 'CEP' },
      { key: 'coordenadas', label: 'Coordenadas' },
      { key: 'roteiro_acesso', label: 'Roteiro de acesso' },
      { key: 'datum', label: 'Datum' },
      { key: 'confrontacoes', label: 'Confrontações' },
    ],
  },
  {
    titulo: 'Áreas',
    icon: Ruler,
    campos: [
      { key: 'area_total_ha', label: 'Área total (ha)' },
      { key: 'area_lavoura_ha', label: 'Área de lavoura (ha)' },
      { key: 'area_pastagem_ha', label: 'Pastagem (ha)' },
      { key: 'area_reserva_legal_ha', label: 'Reserva legal (ha)' },
      { key: 'area_app_ha', label: 'APP (ha)' },
      { key: 'perimetro_m', label: 'Perímetro (m)' },
    ],
  },
  {
    titulo: 'CAR',
    icon: Trees,
    campos: [
      { key: 'car_codigo', label: 'Código CAR' },
      { key: 'car_registro', label: 'Registro CAR' },
      { key: 'car_nome_imovel', label: 'Nome do imóvel (CAR)' },
      { key: 'car_matricula', label: 'Matrícula (CAR)' },
      { key: 'car_latitude', label: 'Latitude (CAR)' },
      { key: 'car_longitude', label: 'Longitude (CAR)' },
      { key: 'car_area_consolidada', label: 'Área consolidada' },
      { key: 'car_vegetacao_nativa', label: 'Vegetação nativa' },
      { key: 'car_reserva_legal', label: 'Reserva legal (CAR)' },
      { key: 'car_app', label: 'APP (CAR)' },
    ],
  },
  {
    titulo: 'CCIR / INCRA',
    icon: Receipt,
    campos: [
      { key: 'ccir_status', label: 'Status' },
      { key: 'ccir_ano', label: 'Ano' },
      { key: 'ccir_codigo_imovel', label: 'Código do imóvel' },
      { key: 'ccir_numero', label: 'Número' },
      { key: 'ccir_modulos_rurais', label: 'Módulos rurais' },
      { key: 'ccir_modulos_fiscais', label: 'Módulos fiscais' },
      { key: 'ccir_data_geracao', label: 'Data geração' },
      { key: 'ccir_data_vencimento', label: 'Data vencimento' },
    ],
  },
  {
    titulo: 'SIGEF / Georreferenciamento',
    icon: Compass,
    campos: [
      { key: 'sigef_codigo', label: 'Código SIGEF' },
      { key: 'geo_codigo_sigef', label: 'Código GEO/SIGEF' },
      { key: 'geo_status', label: 'Status GEO' },
      { key: 'geo_data_averbacao', label: 'Data averbação' },
      { key: 'geo_numero_averbacao', label: 'Número averbação' },
      { key: 'art_numero', label: 'ART' },
    ],
  },
  {
    titulo: 'Cartório',
    icon: Building2,
    campos: [
      { key: 'cartorio', label: 'Cartório' },
      { key: 'municipio_cartorio', label: 'Município do cartório' },
      { key: 'numero_matricula_anterior', label: 'Matrícula anterior' },
      { key: 'data_certidao', label: 'Data da certidão' },
      { key: 'validade_certidao', label: 'Validade da certidão' },
      { key: 'onus', label: 'Ônus' },
    ],
  },
  {
    titulo: 'NIRF / ITR',
    icon: ShieldCheck,
    campos: [
      { key: 'nirf', label: 'NIRF' },
      { key: 'itr_status', label: 'Status' },
      { key: 'itr_ano', label: 'Ano' },
      { key: 'itr_area_total', label: 'Área total (ITR)' },
      { key: 'itr_valor_total', label: 'Valor total' },
    ],
  },
  {
    titulo: 'Licença ambiental',
    icon: Leaf,
    campos: [
      { key: 'lic_ambiental_numero', label: 'Número' },
      { key: 'lic_ambiental_tipo', label: 'Tipo' },
      { key: 'lic_ambiental_orgao', label: 'Órgão' },
      { key: 'lic_ambiental_atividade', label: 'Atividade' },
      { key: 'lic_ambiental_data_emissao', label: 'Emissão' },
      { key: 'lic_ambiental_data_validade', label: 'Validade' },
    ],
  },
];

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') return Number.isNaN(v);
  return false;
}

function formatValue(v: unknown): string {
  if (isEmpty(v)) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (v instanceof Date) return v.toLocaleDateString('pt-BR');
  // ISO date string
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v as string)) {
    try {
      return new Date(v as string).toLocaleDateString('pt-BR');
    } catch {
      return v as string;
    }
  }
  return JSON.stringify(v);
}

export function ImovelCadastroTab({ imovel, verificacao }: Props) {
  if (!imovel) {
    return (
      <div className="p-6 text-center text-sm text-text-muted">
        Esta oportunidade não está vinculada a um imóvel — sinal aplicado ao cliente.
      </div>
    );
  }

  // Identifica campos críticos vazios para a verificação corrente
  const criticos = (verificacao && CAMPOS_CRITICOS[verificacao]) || [];
  const criticosVazios = criticos.filter((k) => isEmpty(imovel[k]));

  // Total de campos preenchidos (vista de "completude")
  const totalCampos = SECOES.flatMap((s) => s.campos).length;
  const preenchidos = SECOES.flatMap((s) => s.campos).filter(
    (c) => !isEmpty(imovel[c.key]),
  ).length;
  const completude = Math.round((preenchidos / totalCampos) * 100);

  return (
    <div className="flex flex-col gap-4">
      {/* Header com completude e alerta de campos críticos */}
      <div className="flex flex-wrap items-center gap-3 px-1">
        <Chip tone={completude >= 70 ? 'ok' : completude >= 40 ? 'warn' : 'danger'} dot>
          Cadastro {completude}% preenchido
        </Chip>
        <span className="text-[0.78rem] text-text-muted">
          {preenchidos} de {totalCampos} campos
        </span>
      </div>

      {criticosVazios.length > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-warn bg-[rgba(245,158,11,0.06)]">
          <AlertTriangle
            size={18}
            strokeWidth={1.5}
            className="text-warn shrink-0 mt-0.5"
          />
          <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold text-text">
              O sinal pode ser falta de cadastro
            </div>
            <p className="text-[0.82rem] text-text-2">
              Para a verificação <code className="font-mono text-text">{verificacao}</code>,
              estes campos estão em branco no cadastro do imóvel:
            </p>
            <div className="flex flex-wrap gap-1 mt-1">
              {criticosVazios.map((k) => (
                <Chip key={k} tone="warn" size="sm">
                  {k}
                </Chip>
              ))}
            </div>
            <p className="text-[0.78rem] text-text-muted mt-1">
              Antes de classificar, vale checar se o sinal some quando esses dados forem preenchidos.
            </p>
          </div>
        </div>
      )}

      {/* Seções — fundo branco, apenas borda; ícone representa cada seção */}
      <div className="flex flex-col gap-4">
        {SECOES.map((sec) => {
          const campos = sec.campos.map((c) => ({
            ...c,
            valor: imovel[c.key],
            critico: criticos.includes(c.key),
          }));
          const algumPreenchido = campos.some((c) => !isEmpty(c.valor));
          const SectionIcon = sec.icon;
          return (
            <section
              key={sec.titulo}
              className="border border-border rounded-lg bg-elevated"
            >
              <header className="flex items-center gap-2 px-4 py-3">
                <SectionIcon
                  size={15}
                  strokeWidth={1.75}
                  className="text-accent shrink-0"
                />
                <h4 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text">
                  {sec.titulo}
                </h4>
                {algumPreenchido ? (
                  <CheckCircle2
                    size={14}
                    strokeWidth={1.5}
                    className="ml-auto text-ok"
                  />
                ) : (
                  <MinusCircle
                    size={14}
                    strokeWidth={1.5}
                    className="ml-auto text-text-muted"
                  />
                )}
              </header>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 pb-4">
                {campos.map((c) => {
                  const vazio = isEmpty(c.valor);
                  return (
                    <div
                      key={c.key}
                      className={cn(
                        'flex flex-col gap-0.5 min-w-0',
                        c.critico && vazio && 'p-2 -m-2 rounded bg-[rgba(245,158,11,0.05)]',
                      )}
                    >
                      <dt
                        className={cn(
                          'text-[0.7rem] uppercase tracking-wide',
                          c.critico && vazio ? 'text-warn font-semibold' : 'text-text-muted',
                        )}
                      >
                        {c.label}
                        {c.critico && vazio && ' *'}
                      </dt>
                      <dd
                        className={cn(
                          'text-[0.82rem] truncate',
                          vazio ? 'text-text-muted italic' : 'text-text',
                        )}
                        title={vazio ? 'em branco' : String(c.valor)}
                      >
                        {formatValue(c.valor)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          );
        })}
      </div>

      {criticos.length > 0 && (
        <p className="text-[0.72rem] text-text-muted px-1">
          * campos marcados são críticos para a verificação <code className="font-mono">{verificacao}</code>.
        </p>
      )}
    </div>
  );
}
